/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Form } from '@unform/web';
import { LuMinimize2 } from 'react-icons/lu';
import { parseISO, subDays } from 'date-fns';

import Select, { IOption } from '~/components/Select';
import { KrExpand } from '~/components/KoroIcons';
import api from '~/services/api';

import { Entries } from './styles';
import formatDate from '~/utils/formatDate';

interface IDashboardSalesChartProps {
  expand: boolean;
  onToggleExpand: () => void;
  height: number;
  eventId?: number;
}

interface IDailySale {
  date: string;
  organizer_id: number;
  organizer_name: string;
  total: number;
  total_orders: number;
  quantity_tickets: number;
  tickets_cancelled: number;
  total_organizer: number;
  total_discount: number;
  total_koro: number;
  total_gateway: number;
  value_cancelled: number;
  value_refunded: number;
  // campos extras quando filtrado por evento
  event_id?: number;
  event_name?: string;
}

interface IPaginatedResponse<T> {
  data: T[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface ISaleDay {
  day: string;
  value: number;
  totalValue: number;
}

const lastSalesOptions: IOption[] = [
  { id: 1, value: 'Últimos 7 dias', selected: true },
  { id: 2, value: 'Últimos 30 dias', selected: false },
  { id: 3, value: 'Últimos 60 dias', selected: false },
];

const daysMap: Record<number, number> = { 1: 7, 2: 30, 3: 60 };

const DashboardSalesChart: React.FC<IDashboardSalesChartProps> = ({
  expand,
  onToggleExpand,
  height,
  eventId,
}) => {
  const [idDate, setIdDate] = useState(1);
  const [salesDay, setSalesDay] = useState<ISaleDay[]>([]);

  const fetchAllPages = useCallback(
    async (url: string, params: Record<string, any>) => {
      const firstPage = await api.get<IPaginatedResponse<IDailySale>>(url, {
        params: { ...params, page: 1 },
      });

      const allData = [...firstPage.data.data];
      const totalPages = firstPage.data.pages;

      if (totalPages > 1) {
        const promises = [];
        for (let p = 2; p <= totalPages; p += 1) {
          promises.push(
            api.get<IPaginatedResponse<IDailySale>>(url, {
              params: { ...params, page: p },
            })
          );
        }
        const responses = await Promise.all(promises);
        responses.forEach((r) => allData.push(...r.data.data));
      }

      return allData;
    },
    []
  );

  const handleLoad = useCallback(
    async (period: number) => {
      const daysCount = daysMap[period] || 7;

      const url = eventId
        ? `/sales/organizers/daily/event/${eventId}`
        : '/sales/organizers/daily';

      const data = await fetchAllPages(url, { days: daysCount });

      const apiData = data.map((item) => ({
        day: formatDate(parseISO(item.date), 'dd/MM'),
        value: item.quantity_tickets || 0,
        totalValue: item.total_organizer || 0,
      }));

      const filled: ISaleDay[] = [];
      for (let i = daysCount - 1; i >= 0; i -= 1) {
        const day = formatDate(subDays(new Date(), i), 'dd/MM');
        const found = apiData.find((s) => s.day === day);
        filled.push({
          day,
          value: found?.value || 0,
          totalValue: found?.totalValue || 0,
        });
      }

      setSalesDay(filled);
    },
    [eventId, fetchAllPages]
  );

  useEffect(() => {
    handleLoad(idDate);
  }, [handleLoad, idDate]);

  const series = useMemo<ApexAxisChartSeries>(
    () => [
      { name: 'Receita R$', data: salesDay.map((s) => s.totalValue) },
      { name: 'Participantes', data: salesDay.map((s) => s.value) },
    ],
    [salesDay]
  );

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        height: 350,
        type: 'bar',
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      plotOptions: {
        bar: { horizontal: false, columnWidth: '50%' },
      },
      colors: ['#00A3E0', '#ff6900'],
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      yaxis: [
        {
          title: { text: 'Receita R$' },
          min: 0,
          labels: {
            formatter: (value) =>
              `${value.toFixed(2).toString().replace('.', ',')}`,
          },
        },
        {
          opposite: true,
          title: { text: 'Quantidade de inscrições' },
          min: 0,
          labels: {
            formatter: (value) =>
              Number.isInteger(value) ? value.toString() : '',
          },
        },
      ],
      xaxis: {
        categories: salesDay.map((s) => s.day),
      },
    }),
    [salesDay]
  );

  return (
    <Entries className="col-12 bg-white mt-3">
      <Form onSubmit={Function.prototype as any}>
        <div className="zoom px-4 py-3 d-flex align-items-center justify-content-between">
          <h2 className="h3 fw-semibold mb-0">Vendas por dia</h2>{' '}
          <div className="d-flex w-50 justify-content-end">
            <Select
              name="lastSales"
              className="sold-input"
              options={lastSalesOptions}
              onChange={(e: any) => {
                setIdDate(e.id);
              }}
            />
            <div className="d-flex align-items-center ms-4 mt-0">
              <button
                type="button"
                onClick={onToggleExpand}
                className="btn-full bg-transparent"
              >
                {expand ? (
                  <LuMinimize2 size={18} color="#B0B0B0" />
                ) : (
                  <KrExpand size={18} color="#B0B0B0" />
                )}
              </button>
            </div>
          </div>
        </div>
        <div>
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={height}
          />
        </div>
      </Form>
    </Entries>
  );
};

export default DashboardSalesChart;
